import React, { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import {
  TrendingUp, IndianRupee,
  Users, GraduationCap, Cpu,
  ArrowRight, ArrowUpRight,
} from "lucide-react";
import "./WhyCograd.scss";

import img1 from "../assets/nextgen/ng-lab-1.jpg";
import img2 from "../assets/Our-projects/vr for school .webp";
import img3 from "../assets/Our-projects/pm shri schools.webp";

const CARDS = [
  { icon: Users,         accent: "#0041e8", title: "Engaging Students",  desc: "Hands-on AI & technology for 21st-century skills.", href: "/services", image: img1, objPos: "center top" },
  { icon: Cpu,           accent: "#0041e8", title: "Enhancing Lives",     desc: "Digital transformation for future-ready rural India.",               href: "/services", image: img2, objPos: "center 55%" },
  { icon: GraduationCap, accent: "#0041e8", title: "Empowering Teachers", desc: "AI tools & training so teachers can achieve more.",        href: "/services", image: img3, objPos: "center 25%" },
];

function AnimatedNumber({ target, suffix = "%", duration = 1600 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - (1 - p) ** 3;
      setCount(Math.round(eased * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

function WaveSVG() {
  return (
    <svg viewBox="0 0 220 44" fill="none" className="wc__wave">
      <path d="M0 36 C18 24,36 42,58 30 C80 18,96 38,122 26 C148 14,168 36,192 22 L220 18"
        stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeLinecap="round" />
      <circle cx="220" cy="18" r="3.5" fill="rgba(255,255,255,0.5)" />
      <circle cx="220" cy="18" r="6"   fill="rgba(255,255,255,0.14)" />
    </svg>
  );
}

const anim = (d = 0) => ({
  initial:     { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport:    { once: true, margin: "-60px" },
  transition:  { duration: 0.4, delay: d, ease: "easeOut" },
});

export default function WhyCograd() {
  return (
    <section className="wc-wrap">
      <div className="wc-inner">

        <div className="wc__top">
          <motion.div className="wc__chip" {...anim(0)}>✦ WHY COGRAD</motion.div>
          <motion.h2 className="wc__h2" {...anim(0.07)}>
            Transforming Rural Schools Into<br className="wc__h2-br" />
            <span>AI-Powered Learning<br className="wc__h2-br--mob" /> Campuses</span>
          </motion.h2>
          <motion.p className="wc__sub" {...anim(0.14)}>
            We help schools become innovative, affordable, and future-ready
            through AI, smart classrooms, and teacher empowerment.
          </motion.p>
        </div>

        <div className="wc__grid">

          {/* Left stat */}
          <div className="wc__col">
            <motion.div className="wc__stat-card wc__stat-card--l" {...anim(0.1)}>
              <div className="wc__stat-ico"><TrendingUp size={18} /></div>
              <div className="wc__stat-num"><AnimatedNumber target={10} suffix="%" /></div>
              <div className="wc__stat-rule" />
              <p className="wc__stat-txt">Only 10% students in rural schools have access to computers and AI.</p>
              <div className="wc__stat-grow" />
              <div className="wc__wave-wrap"><WaveSVG /></div>
            </motion.div>
            <motion.a href="/services" className="wc__explore" {...anim(0.18)}>
              Explore Innovation <ArrowRight size={13} />
            </motion.a>
          </div>

          {/* 3 image cards */}
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.a
                key={i}
                href={c.href}
                className="wc__img-card"
                {...anim(0.14 + i * 0.09)}
              >
                <div className="wc__img-top">
                  <img src={c.image} alt={c.title} style={c.objPos ? { objectPosition: c.objPos } : undefined} />
                </div>
                <div className="wc__img-bot">
                  <span className="wc__img-ico" style={{ background: c.accent }}>
                    <Icon size={16} color="#fff" />
                  </span>
                  <h3 className="wc__img-title">{c.title}</h3>
                  <p className="wc__img-desc">{c.desc}</p>
                  <span className="wc__img-link">
                    Explore <ArrowUpRight size={11} />
                  </span>
                </div>
              </motion.a>
            );
          })}

          {/* Right stat */}
          <div className="wc__col">
            <motion.div className="wc__stat-card wc__stat-card--r" {...anim(0.36)}>
              <div className="wc__stat-ico"><IndianRupee size={18} /></div>
              <div className="wc__stat-num"><AnimatedNumber target={28} suffix="%" /></div>
              <div className="wc__stat-rule" />
              <p className="wc__stat-txt">28% of Middle School students drop out due to lack of Quality Schools and Financial Constraints.</p>
              <em className="wc__stat-src">as per ASER, 2023</em>
              <div className="wc__stat-grow" />
              <div className="wc__wave-wrap"><WaveSVG /></div>
            </motion.div>
            <motion.a href="/about-us" className="wc__explore" {...anim(0.44)}>
              Explore Affordability <ArrowRight size={13} />
            </motion.a>
          </div>

        </div>
      </div>
    </section>
  );
}
